// src/components/SharedFilesViewer.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { X, File, Image as ImageIcon, Download, Eye, Search } from 'lucide-react';
import { DocumentViewer } from './DocumentViewer';
import { useAuth } from '@/contexts/AuthContext';
import { useEntity } from '@/hooks/useEntity';
import { sharedFileEntityConfig, type SharedFile } from '@/entities/SharedFile';

export function SharedFilesViewer({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const { items, reload } = useEntity<SharedFile>(sharedFileEntityConfig);
  const [files, setFiles] = useState<SharedFile[]>([]);
  const [viewingFile, setViewingFile] = useState<{ name: string; url: string; type: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRoom, setFilterRoom] = useState<string>('all');

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    // Visibility rules:
    // - Group rooms (roomId present): visible to everyone.
    // - Direct shares (targetUserId present): visible if you are the sender or target.
    const filtered = items.filter((f) => {
      if (f.roomId) return true;
      if (!user) return false;
      return f.sharedBy === user.id || f.targetUserId === user.id || f.userId === user.id;
    });
    // sort newest first
    const sorted = [...filtered].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    setFiles(sorted);
  }, [items, user]);

  const filteredFiles = useMemo(() => files.filter(file => {
    const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         file.sharedByName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRoom = filterRoom === 'all' || file.roomId === filterRoom;
    return matchesSearch && matchesRoom;
  }), [files, searchQuery, filterRoom]);

  const rooms = useMemo(() => Array.from(new Set(files
      .filter(f => f.roomId && f.roomName)
      .map(f => ({ id: f.roomId, name: f.roomName }))
      .map(r => JSON.stringify(r))))
    .map(r => JSON.parse(r)), [files]);

  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="w-full max-w-5xl h-[85vh] bg-slate-900 rounded-2xl border-2 border-amber-400/30 shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Shared Files</h2>
                <p className="text-sm text-amber-100">All files shared in your WHIPT Rooms</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Search and Filter */}
            <div className="flex gap-3 mt-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search files..."
                  className="w-full pl-10 pr-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/40 text-sm"
                />
              </div>
              <select
                value={filterRoom}
                onChange={(e) => setFilterRoom(e.target.value)}
                className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-white/40 text-sm"
              >
                <option value="all">All Rooms</option>
                {rooms.map((room) => (
                  <option key={room.id} value={room.id}>{room.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Files List */}
          <div className="flex-1 overflow-y-auto p-6">
            {filteredFiles.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <File className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg mb-2">No files found</p>
                <p className="text-sm">Files shared in WHIPT Rooms will appear here</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredFiles.map((file) => (
                  <div
                    key={file.id}
                    className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 hover:border-amber-500/50 transition-all group"
                  >
                    {/* File icon */}
                    <div className="w-full h-32 bg-slate-700/50 rounded-lg mb-3 flex items-center justify-center">
                      {file.type.startsWith('image/') ? (
                        <img
                          src={file.url}
                          alt={file.name}
                          className="max-w-full max-h-full object-contain rounded-lg"
                        />
                      ) : (
                        <File className="w-12 h-12 text-slate-400" />
                      )}
                    </div>

                    {/* File info */}
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-white truncate" title={file.name}>
                        {file.name}
                      </p>
                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <span>{file.roomName}</span>
                        <span>{new Date(file.timestamp).toLocaleDateString()}</span>
                      </div>
                      <p className="text-xs text-slate-500">
                        Shared by {file.sharedByName}
                      </p>

                      {/* Actions */}
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => setViewingFile({ name: file.name, url: file.url, type: file.type })}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors text-xs"
                        >
                          <Eye className="w-4 h-4" />
                          <span>View</span>
                        </button>
                        <a
                          href={file.url}
                          download={file.name}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors text-xs"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-700 px-6 py-3 bg-slate-900/50">
            <p className="text-xs text-slate-400 text-center">
              {filteredFiles.length} file{filteredFiles.length !== 1 ? 's' : ''} • Files are saved to your WHIPT account
            </p>
          </div>
        </div>
      </div>

      {/* Document Viewer */}
      {viewingFile && (
        <DocumentViewer
          file={viewingFile}
          onClose={() => setViewingFile(null)}
        />
      )}
    </>
  );
}
